import { Controller, useFormContext } from '@astro/forms'
import { DateField, TimeField } from '@astro/date-field'

/** Resolve a possibly nested error ("education.0.degree") to its message. */
export function getErrorMessage(errors, name) {
  const leaf = name.split('.').reduce((acc, key) => (acc == null ? acc : acc[key]), errors)
  return leaf?.message ? String(leaf.message) : null
}

/** Options may be plain strings or {value, label} (e.g. lookup id + display name). */
const normalizeOptions = (options) =>
  options.map((o) => (typeof o === 'object' ? o : { value: String(o), label: String(o) }))

export function Field({ label, hint, error, children }) {
  return (
    <div>
      {label && (
        <label className="ar-label">
          {label}
          {hint && <span className="normal-case font-normal text-stone-400"> {hint}</span>}
        </label>
      )}
      {children}
      {error && <p className="ar-error">{error}</p>}
    </div>
  )
}

export function TextInput({ name, rules, label, hint, type = 'text', digitsOnly = false, ...props }) {
  const { register, control, formState: { errors } } = useFormContext()
  const error = getErrorMessage(errors, name)

  // Date and time route through the shared pickers instead of the native
  // controls; they need a Controller since their value isn't a DOM ref read.
  if (type === 'date' || type === 'time') {
    const Picker = type === 'date' ? DateField : TimeField
    return (
      <Field label={label} hint={hint} error={error}>
        <Controller
          name={name}
          control={control}
          rules={rules}
          render={({ field }) => (
            <Picker
              value={field.value || ''}
              onChange={(e) => field.onChange(e.target.value)}
              disabled={props.readOnly || props.disabled}
              className={`ar-input${error ? ' has-error' : ''}`}
            />
          )}
        />
      </Field>
    )
  }

  const registration = register(name, rules)
  // Strip non-digits before the form handler reads the value, so letters
  // never appear even transiently (typed or pasted).
  const handleChange = (e) => {
    if (digitsOnly) e.target.value = e.target.value.replace(/\D/g, '')
    registration.onChange(e)
  }
  return (
    <Field label={label} hint={hint} error={error}>
      <input
        type={type}
        {...registration}
        {...props}
        inputMode={digitsOnly ? 'numeric' : props.inputMode}
        onChange={handleChange}
        className={`ar-input${error ? ' has-error' : ''}`}
      />
    </Field>
  )
}

export function TextAreaInput({ name, rules, label, hint, ...props }) {
  const { register, formState: { errors } } = useFormContext()
  const error = getErrorMessage(errors, name)
  return (
    <Field label={label} hint={hint} error={error}>
      <textarea {...register(name, rules)} {...props} className={`ar-input${error ? ' has-error' : ''}`} />
    </Field>
  )
}

/** Checkbox group collected into one array value (e.g. skills, languages). */
export function CheckGroup({ name, rules, label, hint, options }) {
  const { register, formState: { errors } } = useFormContext()
  const error = getErrorMessage(errors, name)
  return (
    <Field label={label} hint={hint} error={error}>
      <div className="flex flex-wrap gap-x-5 gap-y-2.5 pt-1">
        {normalizeOptions(options).map((option) => (
          <label key={option.value} className="flex items-center gap-2 text-xs text-stone-600 cursor-pointer select-none">
            <input type="checkbox" value={option.value} className="ar-check" {...register(name, rules)} /> {option.label}
          </label>
        ))}
      </div>
    </Field>
  )
}

/** Multi-select rendered as toggleable badge pills (checkbox group under the hood). */
export function BadgeGroup({ name, rules, label, hint, options }) {
  const { register, formState: { errors } } = useFormContext()
  const error = getErrorMessage(errors, name)
  return (
    <Field label={label} hint={hint} error={error}>
      <div className="flex flex-wrap gap-2 pt-1">
        {normalizeOptions(options).map((option) => (
          <label key={option.value} className="cursor-pointer select-none">
            <input type="checkbox" value={option.value} className="peer sr-only" {...register(name, rules)} />
            <span className="inline-block rounded-lg px-3 py-1.5 text-xs font-semibold bg-goldPrimary/15 text-amber-800 transition-all hover:bg-goldPrimary/25 peer-checked:bg-goldAccent peer-checked:text-white peer-checked:shadow-md peer-checked:shadow-goldAccent/30 peer-focus-visible:outline-2 peer-focus-visible:outline-goldPrimary/70">
              {option.label}
            </span>
          </label>
        ))}
      </div>
    </Field>
  )
}

/** Radio group storing one string value (e.g. consultation mode). */
export function RadioGroup({ name, rules, label, hint, options }) {
  const { register, formState: { errors } } = useFormContext()
  const error = getErrorMessage(errors, name)
  return (
    <Field label={label} hint={hint} error={error}>
      <div className="flex flex-wrap gap-x-5 gap-y-2.5 pt-1">
        {normalizeOptions(options).map((option) => (
          <label key={option.value} className="flex items-center gap-2 text-xs text-stone-600 cursor-pointer select-none">
            <input type="radio" value={option.value} className="ar-radio" {...register(name, rules)} /> {option.label}
          </label>
        ))}
      </div>
    </Field>
  )
}
